import Footer from "@/components/nav/Footer";
import { useLoggedInUser } from "@/components/providers/ApiProvider/hooks";
import ContainerWrapper from "@/components/ui/ContainerWrapper";
import { FullScreenLoader } from "@/components/ui/LoadingIndicator";
import PlanCard from "@/components/ui/plan/PlanCard";
import PricePeriodControl from "@/components/ui/plan/PricePeriodControl";
import PlanData from "@/data/plans.json";
import { useCheckoutControllerGetSubscriptionCheckoutSession } from "@/lib/lutely-api/gen/checkout/checkout";
import {
  clearPendingSubscription,
  getPendingSubscription,
} from "@/lib/storage-utils";
import { Button, Flex, Heading, Text } from "@radix-ui/themes";
import { useEffect, useState } from "react";

const MAX_AGE_MS = 3 * 60 * 1000; // 3 minutes

export function SubscriptionsPage() {
  const [period, setPeriod] = useState<"yearly" | "monthly">("yearly");
  const [isStripeLoading, setIsStripeLoading] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const { data: user, subscription, isLoading } = useLoggedInUser();
  const { mutateAsync: getCheckoutSession, isIdle } =
    useCheckoutControllerGetSubscriptionCheckoutSession();

  useEffect(() => {
    if (isLoading || !user || !isIdle) return;

    const raw = getPendingSubscription();
    clearPendingSubscription();
    if (!raw || subscription) return;

    let pending: {
      subscriptionIdentifier:
        | "CreatorMonthly"
        | "CreatorAnnual"
        | "LabelMonthly"
        | "LabelAnnual";
      timestamp: number;
    };

    try {
      pending = JSON.parse(raw);
    } catch {
      return;
    }

    if (
      typeof pending.timestamp !== "number" ||
      Date.now() - pending.timestamp > MAX_AGE_MS ||
      !pending.subscriptionIdentifier
    ) {
      return;
    }

    setIsStripeLoading(true);
    (async () => {
      try {
        const result = await getCheckoutSession({
          subscriptionIdentifier: pending.subscriptionIdentifier,
          data: {},
        });

        window.location.href = result.url;
      } catch (err) {
        console.error("Failed to resume subscription checkout", err);
      } finally {
        setIsStripeLoading(false);
      }
    })();
  }, [user, isLoading, getCheckoutSession, subscription, isIdle]);

  if (isLoading || isStripeLoading || isCheckoutLoading) {
    return <FullScreenLoader />;
  }

  return (
    <>
      <ContainerWrapper>
        <Flex
          direction="column"
          className="py-[60px] items-center justify-center"
        >
          <Heading
            className="text-[36px] lg:text-[48px] font-bold text-blue-900"
            data-testid="upgrade-heading"
          >
            {subscription && user ? "Upgrade your plan" : "Choose your plan"}
          </Heading>

          <Flex direction="column" className="pt-[60px] w-full items-center">
            <PricePeriodControl period={period} setPeriod={setPeriod} />

            <Flex
              justify="center"
              align="center"
              className="gap-[12px] pt-[32px] max-w-[1200px] w-full flex-col lg:flex-row lg:items-stretch"
            >
              {PlanData.filter((e) => !subscription || e.title !== "Flex").map(
                (plan) => (
                  <PlanCard
                    key={plan.title}
                    period={period}
                    planData={plan}
                    onCheckoutStart={() => setIsCheckoutLoading(true)}
                    onCheckoutError={() => setIsCheckoutLoading(false)}
                  />
                ),
              )}
            </Flex>

            <Flex className="mt-[32px] p-[16px] pl-[48px] pr-[48px] bg-white items-center">
              <Text className="text-[14px]">
                For <b>Enterprise</b> plans please{" "}
                <Button
                  asChild
                  variant="ghost"
                  size="2"
                  className="pl-[7px] text-[14px] h-[30px] text-brand-primary"
                >
                  <a href="mailto:support@lutely.com">Contact Us</a>
                </Button>
              </Text>
            </Flex>
          </Flex>
        </Flex>
      </ContainerWrapper>
      <Footer />
    </>
  );
}
